import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  XCircleIcon,
  FunnelIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline';
import { mcpChecksApi } from '../lib/api';
import clsx from 'clsx';

interface McpCheck {
  id: string;
  source: string;
  tool: string;
  channel: string | null;
  status: string;
  flagCount: number;
  flags: any[];
  summary: string | null;
  reviewStatus: string;
  reviewedBy: string | null;
  reviewedAt: string | null;
  processingTime: number | null;
  rulesetVersion: string | null;
  createdAt: string;
  team: { name: string };
  apiKey: { name: string; keyPreview: string };
}

const STATUS_STYLES: Record<string, { bg: string; text: string; icon: any }> = {
  COMPLIANT: { bg: 'bg-green-100', text: 'text-green-800', icon: CheckCircleIcon },
  FLAGS_FOUND: { bg: 'bg-amber-100', text: 'text-amber-800', icon: ExclamationTriangleIcon },
  REVIEW_RECOMMENDED: { bg: 'bg-red-100', text: 'text-red-800', icon: XCircleIcon },
};

const REVIEW_STYLES: Record<string, { bg: string; text: string }> = {
  AUTO_CLEARED: { bg: 'bg-gray-100', text: 'text-gray-700' },
  PENDING_REVIEW: { bg: 'bg-amber-100', text: 'text-amber-800' },
  ESCALATED: { bg: 'bg-red-100', text: 'text-red-800' },
  APPROVED: { bg: 'bg-green-100', text: 'text-green-800' },
  REJECTED: { bg: 'bg-red-100', text: 'text-red-800' },
};

const TOOL_LABELS: Record<string, string> = {
  check_compliance: 'General',
  check_email_compliance: 'Email',
  check_social_compliance: 'Social',
  generate_disclosures: 'Disclosures',
  compliance_summary: 'Summary',
};

export default function McpChecksPage() {
  const queryClient = useQueryClient();
  const [statusFilter, setStatusFilter] = useState('');
  const [toolFilter, setToolFilter] = useState('');
  const [page, setPage] = useState(1);
  const [expandedCheck, setExpandedCheck] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['mcp-checks', statusFilter, toolFilter, page],
    queryFn: async () => {
      const params: any = { page, limit: 20 };
      if (statusFilter) params.status = statusFilter;
      if (toolFilter) params.tool = toolFilter;
      const response = await mcpChecksApi.listChecks(params);
      return response.data;
    },
  });

  const checks: McpCheck[] = data?.checks || [];
  const pagination = data?.pagination || { page: 1, pages: 1, total: 0 };

  const reviewMutation = useMutation({
    mutationFn: ({ id, reviewStatus }: { id: string; reviewStatus: 'APPROVED' | 'REJECTED' }) =>
      mcpChecksApi.reviewCheck(id, reviewStatus),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mcp-checks'] });
      toast.success('Review updated');
    },
    onError: () => {
      toast.error('Failed to update review');
    },
  });

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-navy-900">MCP Compliance Checks</h1>
          <p className="mt-1 text-gray-500">
            Review compliance checks initiated by AI assistants (Claude, ChatGPT, Copilot, Perplexity) via the MCP server.
          </p>
        </div>
        <div className="text-right">
          <p className="text-sm text-gray-500">{pagination.total} total checks</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white shadow rounded-lg px-6 py-4">
        <div className="flex items-center space-x-4">
          <FunnelIcon className="w-5 h-5 text-gray-400" />
          <select
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
            className="border border-gray-300 rounded-md px-3 py-1.5 text-sm"
          >
            <option value="">All statuses</option>
            <option value="COMPLIANT">Compliant</option>
            <option value="FLAGS_FOUND">Flags Found</option>
            <option value="REVIEW_RECOMMENDED">Review Recommended</option>
          </select>
          <select
            value={toolFilter}
            onChange={(e) => { setToolFilter(e.target.value); setPage(1); }}
            className="border border-gray-300 rounded-md px-3 py-1.5 text-sm"
          >
            <option value="">All tools</option>
            <option value="check_compliance">General Compliance</option>
            <option value="check_email_compliance">Email Compliance</option>
            <option value="check_social_compliance">Social Compliance</option>
            <option value="generate_disclosures">Disclosure Generation</option>
            <option value="compliance_summary">Summary</option>
          </select>
        </div>
      </div>

      {/* Checks List */}
      <div className="bg-white shadow rounded-lg divide-y divide-gray-200">
        {isLoading ? (
          <div className="p-12 text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-ocean-600" />
            <p className="text-gray-500 mt-4">Loading checks...</p>
          </div>
        ) : checks.length === 0 ? (
          <div className="p-12 text-center">
            <ShieldCheckIcon className="w-12 h-12 mx-auto text-gray-300 mb-4" />
            <p className="text-gray-500 font-medium">No MCP compliance checks yet</p>
            <p className="text-sm text-gray-400 mt-1">
              Checks will appear here when an AI assistant uses the Clarid MCP tools.
            </p>
          </div>
        ) : (
          checks.map((check) => {
            const statusStyle = STATUS_STYLES[check.status] || STATUS_STYLES.FLAGS_FOUND;
            const reviewStyle = REVIEW_STYLES[check.reviewStatus] || REVIEW_STYLES.AUTO_CLEARED;
            const StatusIcon = statusStyle.icon;
            const isExpanded = expandedCheck === check.id;
            const needsReview = ['PENDING_REVIEW', 'ESCALATED'].includes(check.reviewStatus);

            return (
              <div key={check.id}>
                <div
                  className={clsx(
                    'px-6 py-4 cursor-pointer hover:bg-gray-50 transition-colors',
                    needsReview && 'border-l-4 border-amber-400'
                  )}
                  onClick={() => setExpandedCheck(isExpanded ? null : check.id)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4 min-w-0">
                      <StatusIcon className={clsx('w-5 h-5 flex-shrink-0', statusStyle.text)} />
                      <div className="min-w-0">
                        <div className="flex items-center space-x-2">
                          <span className={clsx(
                            'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium',
                            statusStyle.bg, statusStyle.text
                          )}>
                            {check.status.replace('_', ' ')}
                          </span>
                          <span className="text-xs font-medium text-gray-500 bg-gray-100 px-2 py-0.5 rounded">
                            {TOOL_LABELS[check.tool] || check.tool}
                          </span>
                          {check.channel && (
                            <span className="text-xs text-gray-400">{check.channel}</span>
                          )}
                        </div>
                        <p className="text-sm text-gray-600 mt-1 truncate">
                          {check.summary || `${check.flagCount} flag${check.flagCount !== 1 ? 's' : ''} found`}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-3 flex-shrink-0 ml-4">
                      <span className={clsx(
                        'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium',
                        reviewStyle.bg, reviewStyle.text
                      )}>
                        {check.reviewStatus.replace('_', ' ')}
                      </span>
                      <div className="text-right">
                        <p className="text-xs text-gray-500">{formatDate(check.createdAt)}</p>
                        <p className="text-xs text-gray-400">{check.apiKey?.name}</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Expanded Detail */}
                {isExpanded && (
                  <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div>
                        <p className="text-xs font-medium text-gray-500 uppercase">Team</p>
                        <p className="text-sm text-gray-900">{check.team?.name}</p>
                      </div>
                      <div>
                        <p className="text-xs font-medium text-gray-500 uppercase">API Key</p>
                        <p className="text-sm text-gray-900">{check.apiKey?.name} ({check.apiKey?.keyPreview})</p>
                      </div>
                      <div>
                        <p className="text-xs font-medium text-gray-500 uppercase">Processing Time</p>
                        <p className="text-sm text-gray-900">{check.processingTime ? `${check.processingTime}ms` : 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-xs font-medium text-gray-500 uppercase">Source</p>
                        <p className="text-sm text-gray-900">{check.source}</p>
                      </div>
                    </div>

                    {/* Flags */}
                    {check.flags && (check.flags as any[]).length > 0 && (
                      <div className="mb-4">
                        <p className="text-xs font-medium text-gray-500 uppercase mb-2">Compliance Flags</p>
                        <div className="space-y-2">
                          {(check.flags as any[]).map((flag: any, idx: number) => (
                            <div key={idx} className={clsx(
                              'p-3 rounded-md border text-sm',
                              flag.severity === 'critical' ? 'bg-red-50 border-red-200' :
                              flag.severity === 'warning' ? 'bg-amber-50 border-amber-200' :
                              'bg-blue-50 border-blue-200'
                            )}>
                              <div className="flex items-center justify-between">
                                <span className="font-medium">{flag.rule_name || flag.rule_id}</span>
                                <span className={clsx(
                                  'text-xs font-medium px-2 py-0.5 rounded',
                                  flag.severity === 'critical' ? 'bg-red-100 text-red-800' :
                                  flag.severity === 'warning' ? 'bg-amber-100 text-amber-800' :
                                  'bg-blue-100 text-blue-800'
                                )}>
                                  {flag.severity}
                                </span>
                              </div>
                              <p className="text-gray-600 mt-1">{flag.description}</p>
                              {flag.citation && (
                                <p className="text-xs text-gray-400 mt-1">{flag.citation}</p>
                              )}
                              {flag.suggested_fix && (
                                <p className="text-xs text-ocean-700 mt-1 font-medium">Fix: {flag.suggested_fix}</p>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Review Actions */}
                    {needsReview && (
                      <div className="flex items-center space-x-3 pt-3 border-t border-gray-200">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            reviewMutation.mutate({ id: check.id, reviewStatus: 'APPROVED' });
                          }}
                          className="inline-flex items-center px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-md hover:bg-green-700 transition-all"
                        >
                          <CheckCircleIcon className="w-4 h-4 mr-1" />
                          Approve
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            reviewMutation.mutate({ id: check.id, reviewStatus: 'REJECTED' });
                          }}
                          className="inline-flex items-center px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-md hover:bg-red-700 transition-all"
                        >
                          <XCircleIcon className="w-4 h-4 mr-1" />
                          Reject
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Pagination */}
      {pagination.pages > 1 && (
        <div className="flex items-center justify-between">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            className="px-4 py-2 border border-gray-300 text-sm font-medium rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Previous
          </button>
          <span className="text-sm text-gray-500">
            Page {page} of {pagination.pages}
          </span>
          <button
            onClick={() => setPage((p) => Math.min(pagination.pages, p + 1))}
            disabled={page === pagination.pages}
            className="px-4 py-2 border border-gray-300 text-sm font-medium rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}
